import { useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet, Platform, Image, Pressable, useWindowDimensions, Linking } from "react-native";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";

// Shop lives on a standalone Express service at /shop (separate from the Expo
// router), so we Linking.openURL instead of router.push.
const SHOP_URL = "https://dashhound.app/shop";
const openShop = () => Linking.openURL(SHOP_URL);

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";

/** Shared marketing-site navigation. Used on the public homepage AND the blog. */
export function PublicNav() {
  const router = useRouter();
  const isWide = useWindowDimensions().width >= 768;
  const [menuOpen, setMenuOpen] = useState(false);
  const [fcOpen, setFcOpen] = useState(false);

  // Single source of truth for the "FAST CAT" dropdown (web) / group (mobile).
  const FASTCAT_LINKS = [
    { label: "What is Fast CAT", to: "/(auth)/what-is-fast-cat/" },
    { label: "Join Fast CAT", to: "/(auth)/how-to-signup/" },
    { label: "Fast CAT Rankings", to: "/(auth)/fast-cat-rankings/" },
    { label: "Fast CAT Events", to: "/(auth)/fast-cat-events/" },
    { label: "Calculators", to: "/(auth)/tools/" },
  ];

  const closeAnd = (fn: () => void) => {
    setMenuOpen(false);
    fn();
  };
  const goFc = (to: string) => {
    setFcOpen(false);
    router.push(to as any);
  };

  return (
    <View style={styles.navbar}>
      <View style={[styles.navInner, { paddingHorizontal: isWide ? 40 : 20 }]}>
        <TouchableOpacity style={styles.navLogoContainer} onPress={() => router.push("/(auth)/")}>
          {Platform.OS === "web" ? (
            // @ts-ignore — raw <img> on web
            <img src="/logo-text.png" alt="Dash-Hound" style={{ height: 36, objectFit: "contain" }} />
          ) : (
            <Image source={require("../assets/logo.png")} style={{ height: 40, width: 160 }} resizeMode="contain" />
          )}
        </TouchableOpacity>

        {isWide && (
          <View style={styles.navLinks}>
            <View style={{ position: "relative", zIndex: 200 }}>
              <TouchableOpacity onPress={() => setFcOpen((v) => !v)} style={styles.navDropTrigger}>
                <Text style={styles.navLink}>FAST CAT</Text>
                <Ionicons name={fcOpen ? "chevron-up" : "chevron-down"} size={14} color="rgba(255,255,255,0.75)" />
              </TouchableOpacity>
              {fcOpen && (
                <>
                  <Pressable style={styles.ddBackdrop} onPress={() => setFcOpen(false)} />
                  <View style={styles.dropdown}>
                    {FASTCAT_LINKS.map((l) => (
                      <TouchableOpacity key={l.to} style={styles.dropItem} onPress={() => goFc(l.to)}>
                        <Text style={styles.dropItemText}>{l.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </>
              )}
            </View>
            <TouchableOpacity onPress={() => router.push("/(auth)/blog/" as any)}>
              <Text style={styles.navLink}>Blog</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={openShop}>
              <Text style={styles.navLink}>Shop</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => router.push("/(auth)/support/")}>
              <Text style={styles.navLink}>Support</Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.navActions}>
          {isWide ? (
            <>
              <TouchableOpacity onPress={() => router.push("/(auth)/login/")}>
                <Text style={styles.navSignIn}>Sign In</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.navCTA} onPress={() => router.push("/(auth)/register/")}>
                <Text style={styles.navCTAText}>Get Started</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <TouchableOpacity style={styles.navCTA} onPress={() => router.push("/(auth)/register/")}>
                <Text style={styles.navCTAText}>Get Started</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setMenuOpen((v) => !v)}
                style={styles.navHamburger}
                accessibilityLabel={menuOpen ? "Close menu" : "Open menu"}
              >
                <Ionicons name={menuOpen ? "close" : "menu"} size={28} color={WHITE} />
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>

      {!isWide && menuOpen && (
        <>
          <Pressable style={styles.menuBackdrop} onPress={() => setMenuOpen(false)} />
          <View style={styles.mobileMenu}>
            <View style={styles.mobileGroupHeaderRow}>
              <Text style={styles.mobileGroupHeader}>FAST CAT</Text>
            </View>
            {FASTCAT_LINKS.map((l) => (
              <TouchableOpacity key={l.to} style={styles.mobileMenuItem} onPress={() => closeAnd(() => router.push(l.to as any))}>
                <Text style={styles.mobileMenuItemTextAccent}>{l.label}</Text>
                <Ionicons name="chevron-forward" size={18} color={ORANGE} />
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={styles.mobileMenuItem} onPress={() => closeAnd(() => router.push("/(auth)/blog/" as any))}>
              <Text style={styles.mobileMenuItemText}>Blog</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.mobileMenuItem} onPress={() => closeAnd(openShop)}>
              <Text style={styles.mobileMenuItemText}>Shop</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.mobileMenuItem} onPress={() => closeAnd(() => router.push("/(auth)/support/"))}>
              <Text style={styles.mobileMenuItemText}>Support</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.mobileMenuItem, { borderBottomWidth: 0 }]} onPress={() => closeAnd(() => router.push("/(auth)/login/"))}>
              <Text style={styles.mobileMenuItemText}>Sign In</Text>
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  navbar: {
    backgroundColor: NAVY,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
    zIndex: 100,
    ...(Platform.OS === "web" ? ({ position: "sticky", top: 0 } as any) : { paddingTop: 50 }),
  },
  navInner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
  },
  navLogoContainer: { flexDirection: "row", alignItems: "center", gap: 8 },
  navLinks: { flexDirection: "row", gap: 32 },
  navLink: { color: "rgba(255,255,255,0.75)", fontSize: 15, fontWeight: "500" },
  navDropTrigger: { flexDirection: "row", alignItems: "center", gap: 4 },
  ddBackdrop: {
    ...(Platform.OS === "web" ? ({ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 } as any) : StyleSheet.absoluteFillObject),
    zIndex: 150,
  },
  dropdown: {
    position: "absolute",
    top: "100%",
    left: -12,
    marginTop: 10,
    minWidth: 210,
    backgroundColor: NAVY_DARK,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
    paddingVertical: 6,
    zIndex: 200,
    ...(Platform.OS === "web" ? ({ boxShadow: "0 10px 30px rgba(0,0,0,0.35)" } as any) : { shadowColor: "#000", shadowOpacity: 0.35, shadowRadius: 16, shadowOffset: { width: 0, height: 10 }, elevation: 8 }),
  },
  dropItem: { paddingHorizontal: 16, paddingVertical: 11 },
  dropItemText: { color: "rgba(255,255,255,0.9)", fontSize: 14, fontWeight: "600" },
  mobileGroupHeaderRow: { paddingHorizontal: 24, paddingTop: 14, paddingBottom: 4 },
  mobileGroupHeader: { color: "rgba(255,255,255,0.4)", fontSize: 12, fontWeight: "800", letterSpacing: 1, textTransform: "uppercase" },
  navActions: { flexDirection: "row", alignItems: "center", gap: 16 },
  navSignIn: { color: "rgba(255,255,255,0.75)", fontSize: 15, fontWeight: "500" },
  navCTA: { backgroundColor: ORANGE, paddingHorizontal: 18, paddingVertical: 8, borderRadius: 8 },
  navCTAText: { color: NAVY_DARK, fontSize: 14, fontWeight: "800" },
  navHamburger: { padding: 6, marginLeft: 4 },
  menuBackdrop: {
    ...(Platform.OS === "web" ? ({ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 } as any) : StyleSheet.absoluteFillObject),
    backgroundColor: "rgba(0,0,0,0.35)",
    zIndex: 90,
  },
  mobileMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: NAVY_DARK,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
    paddingVertical: 4,
    zIndex: 100,
  },
  mobileMenuItem: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.06)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  mobileMenuItemText: { color: WHITE, fontSize: 16, fontWeight: "500" },
  mobileMenuItemTextAccent: { color: ORANGE, fontSize: 16, fontWeight: "700" },
});
